import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import YouTubeIcon from '@mui/icons-material/YouTube';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import {
  REVIEW_CATEGORIES,
  ReviewPoster,
  ScoreStar,
  normaliseFavoriteRanks,
  normaliseReviewCategories,
  normaliseReviewCategory,
} from './ReviewShared';

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'score-desc', label: 'Score: high to low' },
  { value: 'score-asc', label: 'Score: low to high' },
  { value: 'title-asc', label: 'Title: A–Z' },
  { value: 'title-desc', label: 'Title: Z–A' },
];

const TOP_FIVE_STYLES = {
  1: { border: '#f59e0b', glow: 'rgba(245, 158, 11, 0.28)', badge: '#f59e0b' },
  2: { border: '#fbbf24', glow: 'rgba(251, 191, 36, 0.22)', badge: '#fbbf24' },
  3: { border: '#facc15', glow: 'rgba(250, 204, 21, 0.18)', badge: '#facc15' },
  4: { border: '#fde047', glow: 'rgba(253, 224, 71, 0.16)', badge: '#fde047' },
  5: { border: '#fef08a', glow: 'rgba(254, 240, 138, 0.14)', badge: '#fef08a' },
};

const CATEGORY_STYLES = {
  Anime: { strong: '#6d28d9', soft: '#f3e8ff', text: '#5b21b6' },
  'TV Shows': { strong: '#0f766e', soft: '#ccfbf1', text: '#115e59' },
  Movies: { strong: '#be123c', soft: '#ffe4e6', text: '#9f1239' },
  Manga: { strong: '#c026d3', soft: '#fae8ff', text: '#a21caf' },
  Games: { strong: '#15803d', soft: '#dcfce7', text: '#166534' },
  Books: { strong: '#c2410c', soft: '#ffedd5', text: '#9a3412' },
};

const CATEGORY_BUTTON_STYLE = {
  strong: '#52525b',
  soft: '#f4f4f5',
  text: '#52525b',
};

function dateValue(entry) {
  const value = new Date(entry.createdAt || 0).getTime();
  return Number.isNaN(value) ? 0 : value;
}

function compareEntries(sortBy) {
  return (a, b) => {
    if (sortBy === 'oldest') return dateValue(a) - dateValue(b);
    if (sortBy === 'score-desc') return (b.score || 0) - (a.score || 0);
    if (sortBy === 'score-asc') return (a.score || 0) - (b.score || 0);
    if (sortBy === 'title-asc') return a.title.localeCompare(b.title);
    if (sortBy === 'title-desc') return b.title.localeCompare(a.title);
    return dateValue(b) - dateValue(a);
  };
}

function getVideoLinks(entry) {
  if (Array.isArray(entry.videoLinks) && entry.videoLinks.length > 0) {
    return entry.videoLinks.filter((link) => link?.url);
  }
  return entry.videoUrl ? [{ url: entry.videoUrl, label: '' }] : [];
}

function ReviewRow({ entry, expanded, onToggle, showCategory = false, categoryContext = null }) {
  const entryCategories = normaliseReviewCategories(entry);
  const favoriteRanks = normaliseFavoriteRanks(entry, entryCategories);
  const rank = categoryContext ? Number(favoriteRanks[categoryContext]) : 0;
  const topStyle = TOP_FIVE_STYLES[rank];
  const videoLinks = getVideoLinks(entry);
  const reviewAvailability = entry.review && videoLinks.length > 0
    ? 'Video & Written Review Available'
    : videoLinks.length > 0
    ? 'Video Review Available'
    : entry.review
    ? 'Written Review Available'
    : '';
  const rowDetails = [
    entry.completedDate ? `Completed ${entry.completedDate}` : '',
    reviewAvailability,
  ].filter(Boolean).join(' · ');

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle();
    }
  };

  return (
    <Paper
      component="article"
      elevation={0}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        border: topStyle ? `1px solid ${topStyle.border}` : '1px solid #e4e4e7',
        backgroundColor: '#fff',
        boxShadow: topStyle
          ? `0 0 0 1px ${topStyle.glow}, 0 10px 32px ${topStyle.glow}`
          : '0 6px 20px rgba(15, 23, 42, 0.05)',
        transition: 'transform 160ms ease, box-shadow 160ms ease',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: topStyle
            ? `0 0 0 1px ${topStyle.glow}, 0 14px 38px ${topStyle.glow}`
            : '0 10px 28px rgba(15, 23, 42, 0.09)',
        },
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={onToggle}
        onKeyDown={handleKeyDown}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '64px minmax(0, 1fr) 52px', sm: '88px minmax(0, 1fr) 70px' },
          gap: { xs: 0.8, sm: 1.35 },
          alignItems: 'center',
          p: { xs: 0.75, sm: 1 },
          cursor: 'pointer',
          outline: 'none',
          '&:focus-visible': { boxShadow: 'inset 0 0 0 3px #2563eb' },
        }}
      >
        <ReviewPoster entry={entry} compact={false} />

        <Box sx={{ minWidth: 0 }}>
          {topStyle || showCategory ? (
            <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap" sx={{ mb: 0.8 }}>
              {topStyle ? (
                <Chip
                  label={`#${rank}`}
                  size="small"
                  sx={{
                    height: 25,
                    backgroundColor: topStyle.badge,
                    color: '#302000',
                    fontWeight: 900,
                  }}
                />
              ) : null}
              {showCategory ? entryCategories.map((category) => {
                const categoryStyle = CATEGORY_STYLES[category] || CATEGORY_STYLES.Books;
                return (
                  <Chip
                    key={category}
                    label={category}
                    size="small"
                    sx={{
                      height: 25,
                      backgroundColor: categoryStyle.soft,
                      color: categoryStyle.text,
                      border: `1px solid ${categoryStyle.strong}55`,
                      fontWeight: 850,
                    }}
                  />
                );
              }) : null}
            </Stack>
          ) : null}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 900,
              color: '#18181b',
              lineHeight: 1.2,
              fontSize: { xs: '1rem', sm: '1.15rem' },
              overflowWrap: 'anywhere',
            }}
          >
            {entry.title}
          </Typography>
          {rowDetails ? (
            <Typography variant="caption" sx={{ color: '#71717a', fontWeight: 650 }}>
              {rowDetails}
            </Typography>
          ) : null}
        </Box>

        <Stack alignItems="center" spacing={0}>
          <ScoreStar score={entry.score} compact />
          <ExpandMoreIcon
            aria-hidden="true"
            sx={{
              mt: 0.2,
              color: '#71717a',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 160ms ease',
            }}
          />
        </Stack>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ px: { xs: 1.5, sm: 2.5 }, pb: 2.25 }}>
          <Box sx={{ borderTop: '1px solid #e4e4e7', pt: 1.75 }}>
            {entry.review ? (
              <Typography
                variant="body1"
                sx={{ color: '#3f3f46', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}
              >
                {entry.review}
              </Typography>
            ) : null}
            {videoLinks.length > 0 ? (
              <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: entry.review ? 1.25 : 0 }}>
                {videoLinks.map((link, index) => (
                  <Button
                    key={`${link.url}-${index}`}
                    component="a"
                    size="small"
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => event.stopPropagation()}
                    startIcon={<YouTubeIcon />}
                    variant="contained"
                    sx={{
                      minHeight: 28,
                      px: 1.25,
                      py: 0.25,
                      borderRadius: 999,
                      textTransform: 'none',
                      fontWeight: 850,
                      fontSize: '0.76rem',
                      backgroundColor: '#dc2626',
                      boxShadow: 'none',
                      '&:hover': { backgroundColor: '#b91c1c', boxShadow: 'none' },
                    }}
                  >
                    {link.label || (videoLinks.length === 1 ? 'Watch my review' : `Watch review ${index + 1}`)}
                  </Button>
                ))}
              </Stack>
            ) : null}
            {!entry.review && videoLinks.length === 0 ? (
              <Typography variant="body2" sx={{ color: '#71717a', fontStyle: 'italic' }}>
                No video/written review for this.
              </Typography>
            ) : null}
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
}

function Reviews({ baseUrl }) {
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState(REVIEW_CATEGORIES);
  const [activeCategory, setActiveCategory] = useState('Anime');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('score-desc');
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const autoOpenedCategory = React.useRef(null);
  const isSearchMode = search.trim().length > 0;

  useEffect(() => {
    let cancelled = false;
    const loadReviews = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.get(`${baseUrl.replace(/\/$/, '')}/reviews`);
        if (!cancelled) {
          setEntries((response.data?.entries || []).map((entry) => {
            const entryCategories = normaliseReviewCategories(entry);
            const favoriteRanks = normaliseFavoriteRanks(entry, entryCategories);
            return {
              ...entry,
              categories: entryCategories,
              category: entryCategories[0],
              favoriteRanks,
              favoriteRank: favoriteRanks[entryCategories[0]] ?? null,
            };
          }));
          setCategories(Array.from(new Set(
            (response.data?.categories || REVIEW_CATEGORIES).map(normaliseReviewCategory)
          )));
        }
      } catch (requestError) {
        if (!cancelled) {
          setError('Could not load reviews. Make sure the local server is running.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadReviews();
    return () => { cancelled = true; };
  }, [baseUrl]);

  const visibleGroups = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    const matches = entries
      .filter((entry) => query || normaliseReviewCategories(entry).includes(activeCategory))
      .map((entry) => {
        const titleMatches = entry.title.toLocaleLowerCase().includes(query);
        const alternateTitleMatches = (entry.alternateTitles || [])
          .some((title) => title.toLocaleLowerCase().includes(query));
        const reviewMatches = (entry.review || '').toLocaleLowerCase().includes(query);
        return { entry, relevance: titleMatches || alternateTitleMatches ? 0 : reviewMatches ? 1 : 2 };
      })
      .filter(({ relevance }) => !query || relevance < 2);

    if (query) {
      const searchResults = matches.sort((a, b) => {
        if (a.relevance !== b.relevance) return a.relevance - b.relevance;
        return compareEntries(sortBy)(a.entry, b.entry);
      });
      return {
        topFive: [],
        regular: [],
        searchResults: searchResults.map(({ entry }) => entry),
      };
    }

    const pinTopFive = sortBy === 'score-desc';
    const topFive = pinTopFive
      ? matches
        .filter(({ entry }) => {
          const rank = Number(normaliseFavoriteRanks(entry)[activeCategory]);
          return rank >= 1 && rank <= 5;
        })
        .sort((a, b) => (
          Number(normaliseFavoriteRanks(a.entry)[activeCategory])
          - Number(normaliseFavoriteRanks(b.entry)[activeCategory])
        ))
      : [];

    const regular = matches
      .filter(({ entry }) => !pinTopFive || !normaliseFavoriteRanks(entry)[activeCategory])
      .sort((a, b) => compareEntries(sortBy)(a.entry, b.entry));

    return {
      topFive: topFive.map(({ entry }) => entry),
      regular: regular.map(({ entry }) => entry),
      searchResults: [],
    };
  }, [activeCategory, entries, search, sortBy]);

  const firstVisibleId = visibleGroups.topFive[0]?.id || visibleGroups.regular[0]?.id || null;

  useEffect(() => {
    if (isSearchMode || loading || !firstVisibleId || autoOpenedCategory.current === activeCategory) return;
    setExpandedId(firstVisibleId);
    autoOpenedCategory.current = activeCategory;
  }, [activeCategory, firstVisibleId, isSearchMode, loading]);

  const handleSearchChange = (event) => {
    const nextSearch = event.target.value;
    if (!isSearchMode && nextSearch.trim()) setExpandedId(null);
    setSearch(nextSearch);
  };

  const clearSearch = () => {
    setSearch('');
    setExpandedId(null);
    autoOpenedCategory.current = null;
  };

  const selectCategory = (category) => {
    setActiveCategory(category);
    setSearch('');
    setExpandedId(null);
    autoOpenedCategory.current = null;
  };

  return (
    <Box sx={{ minHeight: 'calc(100vh - 64px)', background: 'linear-gradient(180deg, #f7f7f8 0%, #fff 48%)' }}>
      <Container maxWidth="md" sx={{ py: { xs: 2.5, sm: 4.5 } }}>
        <Box sx={{ textAlign: 'center', mb: 2.25 }}>
          <Typography variant="h3" sx={{ fontWeight: 950, color: '#18181b', letterSpacing: '-0.04em', fontSize: { xs: '1.75rem', sm: '2.25rem' } }}>
            Scores & Reviews
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mt: 0.65,
              color: '#71717a',
              maxWidth: 600,
              mx: 'auto',
              fontSize: { xs: 'clamp(0.48rem, 2.7vw, 0.8rem)', sm: '0.9rem' },
              whiteSpace: 'nowrap',
            }}
          >
            An archive of everything I've consumed, including scores and reviews
          </Typography>
          <Stack direction="row" justifyContent="center" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
            <Button
              component="a"
              href="https://letterboxd.com/eliteari/"
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              variant="outlined"
              endIcon={<OpenInNewRoundedIcon sx={{ fontSize: '0.9rem !important' }} />}
              sx={{
                minHeight: 30,
                px: 1.25,
                borderRadius: 999,
                borderColor: '#334155',
                color: '#334155',
                fontWeight: 850,
                fontSize: '0.74rem',
                textTransform: 'none',
                '&:hover': { borderColor: '#0f172a', backgroundColor: '#f1f5f9' },
              }}
            >
              Letterboxd
            </Button>
            <Button
              component="a"
              href="https://myanimelist.net/profile/EliteAri"
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              variant="outlined"
              endIcon={<OpenInNewRoundedIcon sx={{ fontSize: '0.9rem !important' }} />}
              sx={{
                minHeight: 30,
                px: 1.25,
                borderRadius: 999,
                borderColor: '#2e51a2',
                color: '#2e51a2',
                fontWeight: 850,
                fontSize: '0.74rem',
                textTransform: 'none',
                '&:hover': { borderColor: '#1e3a8a', backgroundColor: '#eff6ff' },
              }}
            >
              MyAnimeList
            </Button>
          </Stack>
        </Box>

        <Paper elevation={0} sx={{ p: 1, mb: 1.25, borderRadius: 3, border: '1px solid #e4e4e7', backgroundColor: '#fff' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.9}>
            <TextField
              fullWidth
              size="small"
              value={search}
              onChange={handleSearchChange}
              placeholder="Search all titles and reviews..."
              inputProps={{ 'aria-label': 'Search all review categories' }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={clearSearch} aria-label="Clear search"><CloseIcon fontSize="small" /></IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 999 } }}
            />
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 190 } }}>
              <InputLabel id="review-sort-label">Sort</InputLabel>
              <Select
                labelId="review-sort-label"
                value={sortBy}
                label="Sort"
                onChange={(event) => {
                  setSortBy(event.target.value);
                  setExpandedId(null);
                }}
                sx={{ borderRadius: 999 }}
              >
                {SORT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Paper>

        <Box
          role="tablist"
          aria-label="Review categories"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))', sm: 'repeat(6, minmax(0, 1fr))' },
            gap: { xs: 0.55, sm: 0.7 },
            mb: 0.7,
          }}
        >
          {categories.map((category) => {
            const active = !isSearchMode && category === activeCategory;
            const colors = CATEGORY_BUTTON_STYLE;
            return (
              <Button
                key={category}
                role="tab"
                aria-selected={active}
                onClick={() => selectCategory(category)}
                sx={{
                  minWidth: 0,
                  minHeight: 34,
                  borderRadius: 999,
                  px: { xs: 0.4, sm: 0.8 },
                  py: 0.45,
                  textTransform: 'none',
                  fontWeight: 850,
                  fontSize: { xs: '0.72rem', sm: '0.76rem' },
                  lineHeight: 1.15,
                  color: active ? '#fff' : colors.text,
                  backgroundColor: active ? colors.strong : colors.soft,
                  border: `1px solid ${active ? colors.strong : `${colors.strong}55`}`,
                  boxShadow: active ? `0 5px 14px ${colors.strong}3d` : 'none',
                  transition: 'transform 150ms ease, box-shadow 150ms ease',
                  '&:hover': {
                    backgroundColor: active ? colors.strong : colors.soft,
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                {category}
              </Button>
            );
          })}
        </Box>

        {loading ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 8 }}>
            <CircularProgress size={30} />
            <Typography variant="body2" sx={{ color: '#71717a' }}>Loading reviews...</Typography>
          </Stack>
        ) : error ? (
          <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert>
        ) : (
          isSearchMode
            ? visibleGroups.searchResults.length
            : visibleGroups.topFive.length + visibleGroups.regular.length
        ) === 0 ? (
          <Paper elevation={0} sx={{ py: 7, px: 2, textAlign: 'center', borderRadius: 3, border: '1px dashed #d4d4d8' }}>
            <Typography variant="h6" sx={{ fontWeight: 850 }}>Nothing found here yet.</Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: '#71717a' }}>
              {isSearchMode ? 'Try a different search.' : `No ${activeCategory.toLowerCase()} reviews have been added.`}
            </Typography>
          </Paper>
        ) : isSearchMode ? (
          <Stack spacing={0.9}>
            <Box sx={{ px: 0.4, pb: 0.15 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#18181b' }}>
                Search results
              </Typography>
              <Typography variant="caption" sx={{ color: '#71717a', fontWeight: 650 }}>
                {visibleGroups.searchResults.length} {visibleGroups.searchResults.length === 1 ? 'match' : 'matches'} across all categories
              </Typography>
            </Box>
            {visibleGroups.searchResults.map((entry) => (
              <ReviewRow
                key={entry.id}
                entry={entry}
                expanded={expandedId === entry.id}
                showCategory
                onToggle={() => setExpandedId((current) => current === entry.id ? null : entry.id)}
              />
            ))}
          </Stack>
        ) : (
          <Stack spacing={1.25}>
            {visibleGroups.topFive.length > 0 ? (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 0.8, sm: 1.1 },
                  borderRadius: 3.5,
                  border: '1px solid #f6c344',
                  background: 'linear-gradient(145deg, #fffbeb 0%, #fffdf5 100%)',
                  boxShadow: '0 10px 30px rgba(245, 158, 11, 0.12)',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={0.8} sx={{ px: 0.35, mb: 0.8 }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: '50%', display: 'grid', placeItems: 'center', backgroundColor: '#f59e0b', color: '#fff' }}>
                    <EmojiEventsRoundedIcon sx={{ fontSize: 17 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 950, color: '#5f3b00', lineHeight: 1.1 }}>
                      Ari's Top 5
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#8a5a00', fontWeight: 650 }}>
                      My favourites in this category
                    </Typography>
                  </Box>
                </Stack>
                <Stack spacing={0.7}>
                  {visibleGroups.topFive.map((entry) => (
                    <ReviewRow
                      key={entry.id}
                      entry={entry}
                      categoryContext={activeCategory}
                      expanded={expandedId === entry.id}
                      onToggle={() => setExpandedId((current) => current === entry.id ? null : entry.id)}
                    />
                  ))}
                </Stack>
              </Paper>
            ) : null}

            {visibleGroups.regular.length > 0 ? (
              <Stack spacing={0.9}>
                {visibleGroups.regular.map((entry) => (
                  <ReviewRow
                    key={entry.id}
                    entry={entry}
                    categoryContext={activeCategory}
                    expanded={expandedId === entry.id}
                    onToggle={() => setExpandedId((current) => current === entry.id ? null : entry.id)}
                  />
                ))}
              </Stack>
            ) : null}
          </Stack>
        )}

        <Box
          component="footer"
          sx={{
            mt: 4,
            pt: 2.25,
            borderTop: '1px solid #e4e4e7',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.2,
            textAlign: { xs: 'center', sm: 'left' },
          }}
        >
          <Box
            component="a"
            href="https://www.themoviedb.org"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit The Movie Database"
            sx={{ display: 'inline-flex', flexShrink: 0 }}
          >
            <Box
              component="img"
              src="https://www.themoviedb.org/assets/2/v4/logos/v2/blue_square_2-d537fb228cf3ded904ef09b136fe3fec72548ebc1fea3fbbd1ad9e36364db38b.svg"
              alt="The Movie Database (TMDB)"
              loading="lazy"
              sx={{ width: 44, height: 'auto', display: 'block' }}
            />
          </Box>
          <Typography variant="caption" sx={{ color: '#71717a', maxWidth: 470, lineHeight: 1.45 }}>
            This product uses the TMDB API but is not endorsed or certified by TMDB.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default Reviews;
